import { cn } from "@lib/cn"
import { CheckCircle2, Info, TriangleAlert, X, XCircle } from "lucide-react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

export type ToastVariant = "success" | "error" | "info" | "warning"

export interface ToastOptions {
  variant?: ToastVariant
  /** Milliseconds before auto-dismiss. 0 = sticky. Default 4000. */
  duration?: number
}

interface ToastEntry {
  id: number
  message: string
  variant: ToastVariant
  duration: number
}

interface ToastContextValue {
  show: (message: string, options?: ToastOptions) => void
  success: (message: string, options?: Omit<ToastOptions, "variant">) => void
  error: (message: string, options?: Omit<ToastOptions, "variant">) => void
  info: (message: string, options?: Omit<ToastOptions, "variant">) => void
  warning: (message: string, options?: Omit<ToastOptions, "variant">) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DEFAULT_DURATION = 4000

const variantClasses: Record<ToastVariant, string> = {
  success: "border-success bg-success-soft text-success-ink",
  error: "border-danger bg-danger-soft text-danger-ink",
  warning: "border-attention bg-attention-soft text-attention-ink",
  info: "border-border bg-surface text-ink",
}

const variantIcon: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: TriangleAlert,
  info: Info,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const counter = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (message: string, options?: ToastOptions) => {
      const id = ++counter.current
      const entry: ToastEntry = {
        id,
        message,
        variant: options?.variant ?? "info",
        duration: options?.duration ?? DEFAULT_DURATION,
      }
      setToasts((prev) => [...prev, entry])
      if (entry.duration > 0) {
        window.setTimeout(() => dismiss(id), entry.duration)
      }
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      dismiss,
      success: (msg, opts) => show(msg, { ...opts, variant: "success" }),
      error: (msg, opts) => show(msg, { ...opts, variant: "error" }),
      info: (msg, opts) => show(msg, { ...opts, variant: "info" }),
      warning: (msg, opts) => show(msg, { ...opts, variant: "warning" }),
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  )
}

interface ViewportProps {
  toasts: ToastEntry[]
  dismiss: (id: number) => void
}

function ToastViewport({ toasts, dismiss }: ViewportProps) {
  // The viewport sits in the top-right corner of the page on md+, and full-
  // width-ish at the top of the screen below md so a thumb-sized close button
  // is reachable.
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed top-3 right-3 left-3 z-50 flex flex-col items-stretch gap-2 md:left-auto md:max-w-sm md:items-end"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  )
}

interface ItemProps {
  toast: ToastEntry
  onDismiss: () => void
}

function ToastItem({ toast, onDismiss }: ItemProps) {
  const Icon = variantIcon[toast.variant]
  const isAlert = toast.variant === "error" || toast.variant === "warning"

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onDismiss])

  return (
    <div
      role={isAlert ? "alert" : "status"}
      aria-live={isAlert ? "assertive" : "polite"}
      className={cn(
        "pointer-events-auto flex items-start gap-2 rounded-xl border p-3 text-sm shadow-md md:min-w-[16rem]",
        variantClasses[toast.variant],
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="flex-1 leading-snug">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Close"
        className={cn(
          "-m-1 ml-1 inline-flex h-7 w-7 items-center justify-center rounded-lg",
          "hover:bg-black/5 focus:ring-2 focus:outline-none",
          "dark:hover:bg-white/10",
        )}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>")
  return ctx
}
